/**
 * The top bar on Free, Personal and Business ("Plan-based top nav and the More
 * tab", Prototype planDemo): the Website sections sit next to Today, and one
 * More tab says what upgrading adds. Pro keeps Today · Campaigns · Social ·
 * Website · Prism AI.
 */

export type WebsiteSection =
  | 'pages'
  | 'articles'
  | 'media'
  | 'authors'
  | 'forms'
  | 'analytics'
  | 'manage';

export const SECTION_TITLES: Record<WebsiteSection, string> = {
  pages: 'Pages',
  articles: 'Articles',
  media: 'Media',
  authors: 'Authors',
  forms: 'Forms',
  analytics: 'Analytics',
  manage: 'Manage',
};

const SECTIONS = Object.keys(SECTION_TITLES) as WebsiteSection[];

/** Which Website section a screen belongs to; null off the Website screens. */
export function websiteSection(path: string, view?: string | null): WebsiteSection | null {
  if (path === '/content') {
    if (view === 'design') return 'manage';
    return SECTIONS.find((s) => s === view) || 'articles';
  }
  // Per-type settings and the old Site paths count as Manage.
  if (
    path === '/manage' ||
    path === '/site' ||
    path.startsWith('/site/') ||
    /^\/content\/(.+\/)?settings$/.test(path)
  ) {
    return 'manage';
  }
  if (path === '/media' || path.startsWith('/media/')) return 'media';
  // An article open or being written stays under Articles.
  if (path.startsWith('/content/')) return 'articles';
  return null;
}

/**
 * The More tab: its own screen, the upgrade screens a link can still land on,
 * and Analytics' upgrade card where Analytics isn't in the nav.
 */
export function moreActive(path: string, view: string | null | undefined, fullAnalytics: boolean) {
  if (path === '/more') return true;
  if (/^\/(reach|campaigns|prism-ai)(\/|$)/.test(path)) return true;
  return !fullAnalytics && websiteSection(path, view) === 'analytics';
}

export type MoreColumn = 'analytics' | 'campaigns' | 'social' | 'prism';

/** Analytics only where it's still an upgrade; Prism AI only once it has launched. */
export function moreColumns(fullAnalytics: boolean, prismLaunched: boolean): MoreColumn[] {
  return [
    ...(fullAnalytics ? [] : (['analytics'] as const)),
    'campaigns' as const,
    'social' as const,
    ...(prismLaunched ? (['prism'] as const) : []),
  ];
}
